import React, { useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { BASE_FINGERING, relativeSwaraForNewSa, type HoleState } from "./bansuri";
import { isKomal, swaraShortLabel } from "./swaraUtils";

export const STATES = [
    { swara: "Ma (Tivra)", y: 260 },
    { swara: "Ma (Shuddha)", y: 280 },
    { swara: "Ga", y: 300 },
    { swara: "komal Ga", y: 320 },
    { swara: "Re", y: 340 },
    { swara: "komal Re", y: 360 },
    { swara: "Sa", y: 385 }, // Base
    { swara: "Ni", y: 410 },
    { swara: "komal Ni", y: 430 },
    { swara: "Dha", y: 450 },
    { swara: "komal Dha", y: 470 },
    { swara: "Pa", y: 490 }
];

function statePct(index: number): number {
    if (STATES.length <= 1) return 50;
    const top = 14;
    const bottom = 86;
    const range = bottom - top;
    return top + (index / (STATES.length - 1)) * range;
}

function getDifficultyConfig(difficulty: string) {
    switch (difficulty) {
        case "Very Easy":
            return { color: "bg-emerald-400", text: "text-emerald-700" };
        case "Easy":
            return { color: "bg-emerald-500", text: "text-emerald-700" };
        case "Medium":
            return { color: "bg-blue-400", text: "text-blue-700" };
        case "Hard":
            return { color: "bg-amber-400", text: "text-amber-700" };
        case "Very Hard":
            return { color: "bg-rose-400", text: "text-rose-700" };
        default:
            return { color: "bg-slate-400", text: "text-slate-700" };
    }
}

function getDifficultyCueColor(difficulty?: string) {
    if (!difficulty) return "bg-slate-400";
    switch (difficulty) {
        case "Very Easy":
        case "Easy":
            return "bg-emerald-500";
        case "Medium":
            return "bg-sky-500";
        case "Hard":
            return "bg-amber-500";
        case "Very Hard":
            return "bg-rose-500";
        default:
            return "bg-slate-400";
    }
}

function findNearestSwara(pct: number): typeof STATES[number] {
    let best = STATES[0]!;
    let bestDist = Infinity;
    for (let i = 0; i < STATES.length; i++) {
        const s = STATES[i]!;
        const sPct = statePct(i);
        const dist = Math.abs(sPct - pct);
        if (dist < bestDist) { bestDist = dist; best = s; }
    }
    return best;
}

export function VerticalFluteSlider({
    fluteScale,
    activeSwara,
    difficulty,
    difficultyBySa,
    onChange,
    noteOptions,
    selectedNote,
    onSelectNote
}: {
    fluteScale?: string;
    activeSwara: string;
    difficulty: string;
    difficultyBySa?: Record<string, string>;
    onChange: (swara: string) => void;
    noteOptions?: string[];
    selectedNote?: string;
    onSelectNote?: (note: string) => void;
}) {
    const activeState = STATES.find(s => s.swara === activeSwara) || STATES[6]!;
    const activeStateIndex = STATES.findIndex((s) => s.swara === activeState.swara);
    const fingering = BASE_FINGERING[activeSwara] || BASE_FINGERING["Sa"]!;
    const difficultyConfig = getDifficultyConfig(difficulty);

    const trackRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);

    const getSwaraFromEvent = useCallback((clientY: number) => {
        if (!trackRef.current) return null;
        const rect = trackRef.current.getBoundingClientRect();
        const pct = ((clientY - rect.top) / rect.height) * 100;
        return findNearestSwara(Math.max(0, Math.min(100, pct)));
    }, []);

    const onPointerDown = (e: React.PointerEvent) => {
        e.preventDefault();
        isDragging.current = true;
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        const state = getSwaraFromEvent(e.clientY);
        if (state && state.swara !== activeSwara) onChange(state.swara);
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (!isDragging.current) return;
        const state = getSwaraFromEvent(e.clientY);
        if (state && state.swara !== activeSwara) onChange(state.swara);
    };

    const onPointerUp = () => { isDragging.current = false; };

    return (
        <div className="relative flex h-[620px] sm:h-[640px] lg:h-full max-h-[850px] w-full items-stretch gap-0 sm:gap-0.5 select-none">
            <div className="flex h-full w-[96px] sm:w-[138px] lg:w-[150px] shrink-0 items-center justify-end">
                {/* Bamboo Flute SVG */}
                <svg viewBox="50 0 60 600" className="h-full w-full drop-shadow-2xl">
                <defs>
                    <linearGradient id="vBambooGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#8b4513" />
                        <stop offset="25%" stopColor="#e0b144" />
                        <stop offset="50%" stopColor="#f4d35e" />
                        <stop offset="75%" stopColor="#e0b144" />
                        <stop offset="100%" stopColor="#9b6a27" />
                    </linearGradient>
                    <pattern id="vBambooGrain" width="40" height="100" patternUnits="userSpaceOnUse">
                        <line x1="2" y1="0" x2="2" y2="100" stroke="#bc6c25" strokeWidth="0.3" opacity="0.4" />
                        <line x1="10" y1="0" x2="10" y2="100" stroke="#bc6c25" strokeWidth="0.2" opacity="0.3" />
                        <line x1="18" y1="0" x2="18" y2="100" stroke="#a0522d" strokeWidth="0.5" opacity="0.3" />
                        <line x1="26" y1="0" x2="26" y2="100" stroke="#bc6c25" strokeWidth="0.2" opacity="0.3" />
                        <line x1="34" y1="0" x2="34" y2="100" stroke="#a0522d" strokeWidth="0.4" opacity="0.4" />
                    </pattern>
                </defs>

                {/* Bamboo body */}
                <rect x="60" y="10" width="40" height="580" fill="url(#vBambooGrad)" stroke="#6f3711" strokeWidth="0.5" rx="4" />
                <rect x="60" y="10" width="40" height="580" fill="url(#vBambooGrain)" style={{ mixBlendMode: "multiply" }} rx="4" />

                {/* Binding rings */}
                <g fill="#780000">
                    <rect x="58" y="30" width="44" height="8" />
                    <rect x="58" y="60" width="44" height="6" />
                    <rect x="58" y="240" width="44" height="4" />
                    <rect x="58" y="400" width="44" height="4" />
                    <rect x="58" y="550" width="44" height="8" />
                </g>

                {/* Blowing hole */}
                <ellipse cx="80" cy="80" rx="8" ry="10" fill="#111111" />
                {fluteScale && (
                    <g aria-label="flute-key-carving">
                        <text
                            x="80"
                            y="111.2"
                            textAnchor="middle"
                            fill="#f5e5b8"
                            fillOpacity="0.45"
                            fontSize="12"
                            fontWeight="700"
                            letterSpacing="0.08em"
                        >
                            {fluteScale}
                        </text>
                        <text
                            x="80"
                            y="112.8"
                            textAnchor="middle"
                            fill="#4d3314"
                            fillOpacity="0.35"
                            fontSize="12"
                            fontWeight="700"
                            letterSpacing="0.08em"
                        >
                            {fluteScale}
                        </text>
                        <text
                            x="80"
                            y="112"
                            textAnchor="middle"
                            fill="#6e4a22"
                            fontSize="12"
                            fontWeight="700"
                            letterSpacing="0.08em"
                        >
                            {fluteScale}
                        </text>
                    </g>
                )}

                {/* Finger holes */}
                {renderVHole(280, fingering[0])}
                {renderVHole(320, fingering[1])}
                {renderVHole(360, fingering[2])}
                {renderVHole(410, fingering[3])}
                {renderVHole(450, fingering[4])}
                {renderVHole(490, fingering[5])}
                </svg>
            </div>

            {/* Slider Track & Thumb */}
            <div className="flex min-w-0 flex-1 flex-col justify-start pl-0 sm:pl-0.5">
                {noteOptions && selectedNote && onSelectNote && (
                    <div className="mb-2 rounded-lg border border-slate-300 bg-white px-2 py-2">
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Your Flute</div>
                        <div className="grid grid-cols-4 gap-1">
                            {noteOptions.map((note) => (
                                <button
                                    key={note}
                                    type="button"
                                    onClick={() => onSelectNote(note)}
                                    className={`rounded-md border px-1 py-1 text-[10px] font-semibold ${selectedNote === note
                                            ? "border-slate-900 bg-slate-900 text-white"
                                            : "border-slate-300 bg-white text-slate-700"
                                        }`}
                                >
                                    {note}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className="hidden sm:block w-full text-[10px] text-slate-600 bg-white/80 rounded-2xl px-3.5 py-3 border border-white/90 shadow-sm mb-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Difficulty</div>
                    <ul className="flex flex-col gap-1.5">
                        <li className="flex items-center gap-2 rounded-md px-2 py-1 bg-emerald-50/70">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="font-medium">Easy</span>
                        </li>
                        <li className="flex items-center gap-2 rounded-md px-2 py-1 bg-sky-50/80">
                            <span className="w-2 h-2 rounded-full bg-sky-500" />
                            <span className="font-medium">Medium</span>
                        </li>
                        <li className="flex items-center gap-2 rounded-md px-2 py-1 bg-amber-50/80">
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                            <span className="font-medium">Hard</span>
                        </li>
                        <li className="flex items-center gap-2 rounded-md px-2 py-1 bg-rose-50/80">
                            <span className="w-2 h-2 rounded-full bg-rose-500" />
                            <span className="font-medium">Very Hard</span>
                        </li>
                    </ul>
                </div>
                <div
                    ref={trackRef}
                    className="relative flex-1 w-full rounded-lg border border-slate-200/80 bg-white/55 px-2"
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerLeave={onPointerUp}
                    onPointerDown={onPointerDown}
                >
                    {STATES.map((state) => {
                        const idx = STATES.findIndex((s) => s.swara === state.swara);
                        const relativeSwara = relativeSwaraForNewSa(state.swara, activeSwara);
                        const absoluteLabel = swaraShortLabel(state.swara);
                        const relativeLabel = swaraShortLabel(relativeSwara);
                        return (
                        <div
                            key={state.swara}
                            onClick={() => onChange(state.swara)}
                            className="absolute left-0 w-full h-5 -mt-2.5 flex items-center cursor-pointer group z-10"
                            style={{ top: `${statePct(idx)}%` }}
                        >
                            <div className="flex-1 flex items-center justify-start pl-2 sm:pl-3">
                                <div className="inline-flex items-center gap-1.5 sm:gap-4 rounded-md px-1.5 py-1 group-hover:bg-white/60">
                                    <span
                                        className={`w-1.5 h-1.5 rounded-full ${getDifficultyCueColor(difficultyBySa?.[state.swara])}`}
                                    />
                                    <span
                                        className="text-[10px] sm:text-xs font-semibold text-slate-600 min-w-[1.3rem] sm:min-w-[2rem] text-left"
                                        style={isKomal(state.swara) ? { textDecorationLine: "underline", textUnderlineOffset: "2px" } : {}}
                                    >
                                        {absoluteLabel}
                                    </span>
                                    <span className="text-[10px] text-slate-400">→</span>
                                    <span
                                        className={`text-[10px] sm:text-sm font-bold transition-all min-w-[1.5rem] sm:min-w-[2.2rem] ${activeSwara === state.swara
                                                ? `${difficultyConfig.text} scale-110 drop-shadow-sm`
                                                : "text-slate-400"
                                            }`}
                                        style={isKomal(relativeSwara) ? { textDecorationLine: "underline", textUnderlineOffset: "2px" } : {}}
                                    >
                                        {relativeLabel}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )})}

                    {/* Draggable Sa Thumb */}
                    <motion.div
                        animate={{ top: `${statePct(activeStateIndex)}%` }}
                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                        className="absolute left-0 sm:left-3 -mt-4 w-auto flex items-center z-20 cursor-grab active:cursor-grabbing touch-none"
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                    >
                        <div className={`${difficultyConfig.color} text-white font-bold text-sm px-3 py-2 sm:px-3 sm:py-1.5 rounded-full shadow-lg flex items-center gap-1 border-2 border-white`}>
                            <span
                                style={isKomal(activeSwara) ? { textDecorationLine: "underline", textUnderlineOffset: "2px" } : {}}
                            >
                                {swaraShortLabel(activeSwara)}
                            </span>
                            <span className="opacity-80">→</span>
                            <span>Sa</span>
                        </div>
                    </motion.div>
                </div>
            </div>

        </div>
    );
}

function renderVHole(cy: number, state: HoleState | undefined) {
    if (!state) return null;
    if (state === "x") return <circle cx="80" cy={cy} r="7.5" fill="#111111" />;
    if (state === "|") {
        return (
            <g>
                <circle cx="80" cy={cy} r="7.5" fill="white" stroke="#111111" strokeWidth="2" />
                <path d={`M 72.5 ${cy} A 7.5 7.5 0 0 1 87.5 ${cy} Z`} fill="#111111" />
            </g>
        );
    }
    return <circle cx="80" cy={cy} r="7.5" fill="white" stroke="#111111" strokeWidth="2" />;
}
